/*
  ts-cart.js — TremShield cart controller. Owns EVERY cart mutation and EVERY repaint.
  Replaces Dawn's assets/cart.js (CartItems / CartRemoveButton) and assets/ts-cart-sync.js.

  ============================================================================
  WHY THIS FILE EXISTS — five confirmed production failures it removes
  ============================================================================
  1. INDEX ADDRESSING. Dawn posted {line: N} (1-based index). The moment anything
     removed or reordered a line, the shopper's next tap hit a line that had moved
     or no longer existed -> Shopify errored -> the stepper desynced from the real
     cart. Filmed: badge 9 / total $179.91 (both CORRECT) while the stepper
     flickered 15 -> 10 -> 3 with a stuck error banner.
     => Here every write is addressed by the immutable line `key`.

  2. "[object Object]" ON SCREEN. Dawn assigned the `errors` OBJECT straight to
     .textContent. => tsErrorText() normalises string | array | object-of-arrays
     | {description} | {message}, and tsSafeText() is the last line of defence.

  3. TWO LAYERS FIGHTING. ts-cart-sync.js monkey-patched window.fetch AND
     XMLHttpRequest, then repainted the drawer 900ms after ANY /cart/* request
     from GET /?sections=... (the HOMEPAGE, not the current page).
     => No monkey-patching here. The controller owns the write, so it knows
     exactly when to paint and paints from that same response.

  4. THE STEPPER WAS DELIBERATELY CLICK-DEAD during every request: Dawn added
     .cart__items--disabled (pointer-events:none) and called activeElement.blur().
     => Neither appears in this file. Ever.

  5. SUCCESSFUL WRITES REPORTED FAILURE. Dawn wrapped the paint in
     CartPerformance.measure (no try/catch), so a paint-time TypeError landed in
     the fetch .catch() and printed the error banner AFTER the server had already
     succeeded. => The paint runs in its own try/catch that tells the shopper
     NOTHING. A paint bug degrades to "numbers look stale", never to a false error.

  Three earlier attempts to fix this by making automatic corrections "smarter"
  each shipped a NEW failure: a ~1.5s under-counted cart (checkout could capture
  $39.98 instead of $159.92), a silently lost pack when both colours split, and a
  ~12-requests-per-tap storm that tripped Shopify's 429 bot challenge and locked
  the owner out of his own store. Hence: ONE writer, ONE in flight, keyed lines.

  ============================================================================
  INVARIANTS — do not break these without re-reading the whole file
  ============================================================================
  * Line keys contain a colon ("4541...:9f0c..."). NEVER build an id selector from
    one — querySelector('#CartDrawer-Item-' + key) throws SyntaxError. Address rows
    only via [data-line-key="..."].
  * At most ONE cart write in flight, process-wide. Taps coalesce last-intent-wins.
  * NEVER paint money the client computed. Volume tiers are server-side.
  * NEVER paint an intermediate state: if more intent is queued, record and skip.
  * NEVER preventDefault a submit of #CartDrawer-Form or #cart. The native form
    post is the one checkout path that survives this file failing to load, and it
    carries updates[] so an uncommitted typed quantity still applies.
  * 429/430 is NEVER retried.
*/
(function () {
  if (window.TSCart) return;

  var ALARM_PRODUCT_ID = 8025089835184;
  /* 2026-08-12: was 10, which a REAL SHOPPER hit just by tapping + a dozen times —
     they got "Too many cart updates in a row" on a perfectly normal cart. That value
     was set when the OLD code fired ~12 requests per tap; writes are now serialized
     (one in flight) and coalesced (last-intent-wins), so the round trip itself already
     caps the rate at ~3-5/sec no matter how fast anyone taps. This is now tuned to sit just
     UNDER Shopify's own cart throttle rather than to police the shopper, and — critically — exceeding it
     PACES the queue silently instead of refusing the write and showing an error. */
  window.TS_BUILD = 'giftlane-5';
  var MAX_WRITES_PER_10S = 20;
  var GIFT_CORRECTIONS_PER_ACTION = 3;
  /* A fetch has no timeout of its own. The gift correction now holds the single write
     lane (see maybeCorrectGifts), so a request that never resolves would freeze every
     later tap for as long as the socket hangs — minutes on a phone that drops its radio.
     A normal correction round trip is 200-900ms, so this only fires on a dead request. */
  var GIFT_WRITE_TIMEOUT_MS = 8000;

  /* ---------------------------------------------------------------- state -- */
  var TS = {
    desired: {},        // lineKey -> quantity the shopper wants (uncommitted)
    dirty: [],          // lineKey FIFO queue
    inFlight: null,     // { key, quantity, gift? } | null — the ONE write lane.
                        // Held by pump() for a quantity write AND by maybeCorrectGifts
                        // for a gift-property write. Anything posting to cart_change_url
                        // MUST hold it, or pump() will race that post (see H2, 8/16).
    lastCart: null,     // last authoritative cart JSON
    stallUntil: 0,
    writeLog: [],
    giftBudget: 0,      // gift corrections remaining for this user action
    rateStrikes: 0,     // consecutive real Shopify 429s
    keyVariant: {},     // line key -> variant id. WRITTEN BY noteVariants() since 8/16;
                        // it was declared-and-read-only before that, which left the
                        // stale-key recovery in write() permanently dead (H3).
    keyAlias: {},       // dead line key -> the key that replaced it (see forwardKey)
    cartSync: null,     // the /cart.js refresh already in flight, if any (see add)
    settleWaiters: [],  // resolvers released when the queue genuinely drains (see settle)
  };

  function dirtyAdd(key) { if (TS.dirty.indexOf(key) === -1) TS.dirty.push(key); }
  function money(cents) { return (cents / 100).toFixed(2); }

  /* --------------------------------------------------------------- errors -- */
  function tsErrorText(payload) {
    if (!payload) return '';
    if (typeof payload === 'string') return payload;
    var e = payload.errors != null ? payload.errors : payload.error;
    if (typeof e === 'string') return e;
    if (Object.prototype.toString.call(e) === '[object Array]') return e.filter(Boolean).join(' ');
    if (e && typeof e === 'object') {
      return Object.keys(e).map(function (k) {
        var v = e[k];
        return Object.prototype.toString.call(v) === '[object Array]' ? v.filter(Boolean).join(' ') : String(v);
      }).filter(Boolean).join(' ');
    }
    if (typeof payload.description === 'string') return payload.description;
    if (typeof payload.message === 'string') return payload.message;
    return '';
  }
  function tsSafeText(v) {
    var s = typeof v === 'string' ? v.trim() : '';
    return s || ((window.cartStrings && window.cartStrings.error) || 'Something went wrong. Please try again.');
  }

  /* ------------------------------------------------------------------ dom -- */
  function itemsScope() {
    return document.getElementById('CartDrawer-CartItems') || document.getElementById('main-cart-items');
  }
  function rowsFor(key) {
    /* both surfaces can be in the DOM at once (drawer exists on /cart too) */
    return Array.prototype.slice.call(document.querySelectorAll('[data-line-key="' + key + '"]'));
  }
  function inputsFor(key) {
    return rowsFor(key).filter(function (el) { return el.classList.contains('quantity__input'); });
  }
  function setLineError(key, text) {
    rowsFor(key).forEach(function (el) {
      var box = el.matches('.cart-item__error') ? el : null;
      if (!box) return;
      var t = box.querySelector('.cart-item__error-text');
      if (t) t.textContent = text || '';
    });
  }
  function clearAllLineErrors() {
    Array.prototype.forEach.call(document.querySelectorAll('.cart-item__error-text'), function (t) { t.textContent = ''; });
  }
  function setCartError(text) {
    ['CartDrawer-CartErrors', 'cart-errors'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.textContent = text || '';
    });
  }
  function announce(text) {
    ['CartDrawer-LiveRegionText', 'cart-live-region-text'].forEach(function (id) {
      var el = document.getElementById(id);
      if (el) el.textContent = text || '';
    });
  }
  function showSpinner(key, on) {
    rowsFor(key).forEach(function (el) {
      if (el.tagName !== 'TR') return;
      var sp = el.querySelector('.loading__spinner');
      if (sp) sp.classList.toggle('hidden', !on);
    });
  }
  function setBusy(on) {
    Array.prototype.forEach.call(document.querySelectorAll('.ts-foot, #main-cart-footer .totals'), function (el) {
      if (on) el.setAttribute('aria-busy', 'true'); else el.removeAttribute('aria-busy');
    });
  }

  /* -------------------------------------------------------- header count --- */
  function setHeaderCount(n) {
    var dot = document.querySelector('[data-ts-cartcount]');
    if (n > 0) {
      if (!dot) {
        var host = document.querySelector('[data-ts-cart]');
        if (!host) return;
        dot = document.createElement('span');
        dot.className = 'nh-cart-dot';
        dot.setAttribute('data-ts-cartcount', '');
        host.appendChild(dot);
      }
      dot.textContent = n;
    } else if (dot) {
      dot.remove();
    }
  }

  /* ------------------------------------------------------- focus + scroll -- */
  var ROLE_SEL = {
    plus: '.quantity__button[name="plus"]',
    minus: '.quantity__button[name="minus"]',
    input: '.quantity__input',
    remove: 'cart-remove-button button, cart-remove-button a',
  };
  function captureFocus() {
    var a = document.activeElement;
    if (!a || !a.closest) return null;
    var row = a.closest('tr[data-line-key]');
    if (!row) return null;
    var role = a.name === 'plus' ? 'plus'
      : a.name === 'minus' ? 'minus'
      : a.classList.contains('quantity__input') ? 'input'
      : a.closest('cart-remove-button') ? 'remove' : null;
    if (!role) return null;
    var siblings = Array.prototype.slice.call(row.parentNode.children);
    return {
      key: row.getAttribute('data-line-key'),
      index: siblings.indexOf(row),
      role: role,
      selStart: role === 'input' && a.setSelectionRange ? a.selectionStart : null,
    };
  }
  function restoreFocus(t) {
    if (!t) return;
    var scope = itemsScope();
    if (!scope) return;
    var row = scope.querySelector('tr[data-line-key="' + t.key + '"]');
    if (!row) {
      var rows = scope.querySelectorAll('tr[data-line-key]');
      if (rows.length) row = rows[Math.min(t.index, rows.length - 1)];
    }
    var el = row && row.querySelector(ROLE_SEL[t.role]);
    if (el) {
      try {
        el.focus({ preventScroll: true });
        if (t.selStart != null && el.setSelectionRange) el.setSelectionRange(t.selStart, t.selStart);
      } catch (e) {}
    }
  }

  /* ------------------------------------------------------------ line keys -- */
  /* CRITICAL, found in testing 2026-08-12: Liquid's {{ item.key }} and the AJAX
     API's item.key are DIFFERENT HASHES for the same line when the line carries
     properties. Liquid gave 45416077983920:eb7a40f5..., /cart.js gave
     45416077983920:4af5fe83..., and POSTing the Liquid one returns
     400 "no valid id or line parameter". Only the AJAX key is a valid write id.

     So the Liquid attribute is a placeholder only. As soon as we hold authoritative
     cart JSON we overwrite every row's data-line-key with the AJAX key, matching by
     POSITION — safe because Liquid renders cart.items in the same order /cart.js
     returns them, and the stamp happens against ONE consistent snapshot, never
     across a mutation. All writes then use the stamped key. */
  /* A line's key is a hash of variant + PROPERTIES, so our own free-gift ladder
     changes it: at qty 10 the lock count goes 2 -> 3, the properties are rewritten,
     and every key we already hold is dead. Reproduced live: taps 5/6/7 after a
     6-pack each returned 400 "no valid id or line parameter" while the stepper
     climbed 11,12,13 and the real cart stayed frozen at 10 / $165.00 -- and Remove
     posts to that same dead key, which is why it stopped working too.
     Fixing the queue alone was not enough: the DOM still handed the dead key back on
     the NEXT tap. So every dead key now forwards to its replacement, forever. */
  /* 2026-08-16 — NEVER FORWARD A KEY THE CART STILL KNOWS.
     stampKeys pairs DOM rows to cart items by POSITION. The moment a cart gains or
     loses a line (two pack sizes now write different gift properties, so they no
     longer merge into one line) the positions shift, and stampKeys records an alias
     pointing a still-LIVE key at a dead one. forwardKey then walked that chain and
     posted the dead key: Shopify answers 400 "no valid id or line parameter", the
     stepper moves and snaps straight back, and Remove dies with it. Reproduced on a
     real 375px session: DOM key 93bda3dc (valid) was forwarded to a9a4a549 (dead).
     A live key is authoritative — return it untouched, and only chase the chain for
     keys that are genuinely gone, stopping at the first live one. */
  function keyIsLive(k, cart) {
    cart = cart || TS.lastCart;
    if (!cart || !cart.items) return false;
    for (var i = 0; i < cart.items.length; i++) if (cart.items[i].key === k) return true;
    return false;
  }

  /* 2026-08-16 (H3) — TS.keyVariant was declared and read but NEVER assigned, so the
     stale-key recovery in write() was dead code: wantedVariant was always undefined,
     its search loop never ran one iteration, and the shopper's tap was deleted from
     the queue in silence — the stepper just snapped back with no error. A dead key's
     only surviving clue is the VARIANT it addressed, so record that for every line of
     every authoritative cart we are handed. Pure bookkeeping: no request, no write. */
  function noteVariants(cart) {
    if (!cart || !cart.items) return;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      /* On an AJAX cart line, item.id is the VARIANT id (item.product_id is the
         product). That is exactly the field the recovery compares against. */
      if (it.key && it.id) TS.keyVariant[it.key] = it.id;
    }
  }

  /* Fallback for a key we were never handed. An AJAX line key is "<variantId>:<hash>",
     so the variant survives even when the hash is dead. parseInt, never the raw string:
     the recovery compares with === against a number out of JSON. */
  function variantOf(key) {
    if (TS.keyVariant[key]) return TS.keyVariant[key];
    var n = parseInt(String(key || '').split(':')[0], 10);
    return n > 0 ? n : null;
  }

  function forwardKey(k) {
    if (keyIsLive(k)) return k;
    for (var i = 0; i < 12 && TS.keyAlias[k]; i++) {
      k = TS.keyAlias[k];
      if (keyIsLive(k)) return k;
    }
    return k;
  }

  /* 2026-09-24 — a leased gift correction rotates ONE line's key, and a tap parked
     during that write still names the old one. Before any repaint nothing forwarded
     it, so it went out dead: one 400 plus a /cart.js refetch before it landed
     (reproduced in an offline harness). Record old -> new from the response JSON
     alone: no DOM, so none of stampKeys' position pairing, and if two lines share the
     variant we do not guess, the same rule as the stalekey recovery in write(). */
  function forwardReplaced(line, cart) {
    if (!line || !cart || !cart.items || keyIsLive(line.key, cart)) return;
    var hit = null, n = 0;
    for (var i = 0; i < cart.items.length; i++) {
      if (cart.items[i].id === line.id) { n++; hit = cart.items[i]; }
    }
    if (n === 1) TS.keyAlias[line.key] = hit.key;
  }

  function stampKeys(cart) {
    if (!cart || !cart.items) return;
    noteVariants(cart);   /* record key -> variant BEFORE any row's key is overwritten */
    ['#CartDrawer-CartItems', '#main-cart-items'].forEach(function (sel) {
      var scope = document.querySelector(sel);
      if (!scope) return;
      var rows = scope.querySelectorAll('tr.cart-item');
      for (var i = 0; i < rows.length && i < cart.items.length; i++) {
        var key = cart.items[i].key;
        var row = rows[i];
        var oldKey = row.getAttribute('data-line-key');
        /* 2026-08-16: only alias a key that is genuinely DEAD. Rows are paired to
           items by position, so on a cart that split or reordered, oldKey is often
           just ANOTHER live line — aliasing it poisons the map and kills that
           line's stepper and Remove. */
        if (oldKey && oldKey !== key && !keyIsLive(oldKey, cart)) TS.keyAlias[oldKey] = key;
        row.setAttribute('data-line-key', key);
        ['.quantity__input', 'cart-remove-button', '.cart-item__error'].forEach(function (s2) {
          var el = row.querySelector(s2);
          if (el) el.setAttribute('data-line-key', key);
        });
        if (oldKey && oldKey !== key && TS.desired[oldKey] != null) {
          TS.desired[key] = TS.desired[oldKey];
          delete TS.desired[oldKey];
          TS.dirty = TS.dirty.map(function (k) { return k === oldKey ? key : k; });
        }
      }
    });
  }

  /* A key is only usable if the authoritative cart knows it. If a tap arrives before
     the first stamp, fall back to resolving the row's position against lastCart. */
  function resolveKey(el, key) {
    if (TS.lastCart && TS.lastCart.items.some(function (i) { return i.key === key; })) return key;
    var row = el && el.closest ? el.closest('tr.cart-item') : null;
    if (!row || !TS.lastCart) return null;
    var rows = row.parentNode.querySelectorAll('tr.cart-item');
    var idx = Array.prototype.indexOf.call(rows, row);
    var item = TS.lastCart.items[idx];
    return item ? item.key : null;
  }

  /* ---------------------------------------------------------------- paint -- */
  function sectionIds() {
    var ids = ['cart-drawer'];
    var i = document.getElementById('main-cart-items');
    var f = document.getElementById('main-cart-footer');
    if (i && i.dataset.id) ids.push(i.dataset.id);
    if (f && f.dataset.id) ids.push(f.dataset.id);
    return ids;
  }
  function swap(srcDoc, selector, targetEl) {
    if (!targetEl) return;
    var src = srcDoc.querySelector(selector);
    if (src) targetEl.innerHTML = src.innerHTML;
  }

  /* Replaces ONLY the subtrees that change. #CartDrawer-Overlay and
     <form id="CartDrawer-Form"> are never touched, so form="CartDrawer-Form" on
     the checkout button stays valid at every instant and the overlay never needs
     re-binding (Dawn re-bound it on a setTimeout after every full swap). */
  function paint(cart, sections) {
    try {
      var count = cart ? cart.item_count : 0;
      var focusToken = captureFocus();
      var items = document.querySelector('cart-drawer-items');
      var scrollTop = items ? items.scrollTop : 0;

      if (sections && sections['cart-drawer']) {
        var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
        swap(doc, '#CartDrawer-CartItems', document.getElementById('CartDrawer-CartItems'));
        swap(doc, '.cart-drawer__footer', document.querySelector('.cart-drawer__footer'));
      }
      var pageItems = document.getElementById('main-cart-items');
      var pageFooter = document.getElementById('main-cart-footer');
      if (sections && pageItems && pageItems.dataset.id && sections[pageItems.dataset.id]) {
        var d2 = new DOMParser().parseFromString(sections[pageItems.dataset.id], 'text/html');
        swap(d2, '.js-contents', pageItems.querySelector('.js-contents'));
      }
      if (sections && pageFooter && pageFooter.dataset.id && sections[pageFooter.dataset.id]) {
        var d3 = new DOMParser().parseFromString(sections[pageFooter.dataset.id], 'text/html');
        swap(d3, '.js-contents', pageFooter.querySelector('.js-contents'));
      }

      var badge = document.querySelector('.ts-cart-count');
      if (badge) badge.textContent = count;
      setHeaderCount(count);

      var host = document.querySelector('cart-drawer');
      if (host) host.classList.toggle('is-empty', count === 0);
      if (items) items.classList.toggle('is-empty', count === 0);
      var pageCartItems = document.querySelector('cart-items');
      if (pageCartItems) pageCartItems.classList.toggle('is-empty', count === 0);
      if (pageFooter) pageFooter.classList.toggle('is-empty', count === 0);

      ['CartDrawer-Checkout', 'checkout'].forEach(function (id) {
        var b = document.getElementById(id);
        if (b) b.toggleAttribute('disabled', count === 0);
      });

      if (cart && cart.items) stampKeys(cart);

      if (items && scrollTop) items.scrollTop = scrollTop;
      restoreFocus(focusToken);
      setBusy(false);
    } catch (e) {
      /* A paint failure must NEVER surface as a cart error (root cause 5). */
      if (window.console && console.error) console.error('[ts-cart] paint', e);
    }
  }

  /* --------------------------------------------------------------- writes -- */
  function rateLimited() {
    var now = Date.now();
    TS.writeLog = TS.writeLog.filter(function (t) { return now - t < 10000; });
    return TS.writeLog.length >= MAX_WRITES_PER_10S;
  }

  function post(url, body) {
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(body),
    }).then(function (res) {
      var ct = res.headers.get('content-type') || '';
      if (ct.indexOf('application/json') === -1) {
        /* Shopify's bot challenge answers with an HTML page */
        return { res: res, payload: null, html: true };
      }
      return res.json().then(function (payload) { return { res: res, payload: payload, html: false }; });
    });
  }

  function classify(r, networkError) {
    if (networkError || !navigator.onLine) return { kind: 'network' };
    var status = r && r.res ? r.res.status : 0;
    if (status === 429 || status === 430 || (r && r.html)) return { kind: 'ratelimit' };
    /* Check the MESSAGE before any status branch: Shopify answers a dead line key
       with 400 (not 422, which is what I first gated on and why this bug survived a
       fix), and the generic branch below both showed the raw string AND reverted the
       stepper, silently dropping taps the shopper had already made. */
    var anyText = r && r.payload ? tsErrorText(r.payload) : '';
    if (/no valid id or line parameter|cannot find variant/i.test(anyText)) return { kind: 'stalekey' };
    if (status === 404) return { kind: 'notfound' };
    if (status >= 200 && status < 300) {
      var errText = r.payload && r.payload.errors ? tsErrorText(r.payload) : '';
      return errText ? { kind: 'quantity', text: errText } : { kind: 'ok' };
    }
    if (status === 422) {
      var t422 = tsErrorText(r.payload);
      /* Shopify answers 422 "no valid id or line parameter" when the line key no
         longer exists. Our OWN gift ladder causes this: line properties are part of
         the key hash, so every tier crossing (12, 18, 24 ...) mints a NEW key and any
         intent already queued under the old one is stale. Seen live: a 26-tap burst
         ended at 49 while the shopper had asked for 55, with the raw Shopify string
         shown as an error. It is not a quantity verdict and it is not the shopper's
         problem, so carry the intent across to the new key instead. */
      return { kind: 'quantity', text: t422 };
    }
    return { kind: 'unknown', text: tsErrorText(r.payload) };
  }

  /* THE FIX for "click to 7, 8, 9 and it stops working".
     A line key hashes variant + PROPERTIES, so the free-gift ladder was killing it:
     at qty 10 the lock count went 2 -> 3, a SEPARATE correction write rewrote the
     properties, and every key held by the page died. Live repro: taps 5/6/7 each
     returned 400 "no valid id or line parameter", the stepper climbed 11,12,13 while
     the cart stayed frozen at 10 / $165.00, and Remove -- which posts to that same
     dead key -- stopped working entirely.
     So the correction is no longer a separate write. The gifts ride ALONG WITH the
     quantity in the same POST: one request, the key changes atomically, and the
     response hands back the new key before the shopper can tap again. There is no
     longer a window in which a dead key exists. */
  function fusedGiftProps(key, quantity) {
    var cart = TS.lastCart;
    if (!cart || !cart.items || quantity <= 0) return null;
    var me = null, firstKey = null, total = 0;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.product_id !== ALARM_PRODUCT_ID) continue;
      if (firstKey === null) firstKey = it.key;
      total += (it.key === key) ? quantity : it.quantity;
      if (it.key === key) me = it;
    }
    /* 2026-09-25 (B4): fuse only onto the line the corrector would pick (giftCarrierKey),
       never onto whichever line happens to be first, or a write to the top line would
       mint a second carrier and the corrector would then have to clear the old one. */
    if (!me || key !== giftCarrierKey(cart)) return null;   /* not the carrier: leave it to the idle corrector */
    var have = me.properties || {}, want = giftProps(total), target = {};
    Object.keys(have).forEach(function (k) { if (k.indexOf('Free gift') !== 0) target[k] = have[k]; });
    Object.keys(want).forEach(function (k) { target[k] = want[k]; });
    Object.keys(have).forEach(function (k) { if (k.indexOf('Free gift') === 0 && !(k in want)) target[k] = ''; });
    return target;
  }

  function write(key, quantity) {
    key = forwardKey(key);
    TS.writeLog.push(Date.now());
    var body = {
      id: key,
      quantity: quantity,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    };
    var gifts = fusedGiftProps(key, quantity);
    if (gifts) body.properties = gifts;
    return post(window.routes.cart_change_url, body).then(function (r) {
      var c = classify(r, false);

      if (c.kind === 'ratelimit') {
        /* A REAL Shopify 429/challenge (my own pacer never reaches here — it just
           delays). Shopify throttles transiently, so the first one is NOT the
           shopper's problem: keep their intent queued, back off, and let the pump
           retry it. Only a SECOND consecutive throttle is worth telling them about,
           and even then we keep it calm. Reported live: a normal shopper tapping +
           got a red "Too many cart updates in a row" on a perfectly good cart. */
        TS.rateStrikes++;
        TS.stallUntil = Date.now() + (TS.rateStrikes === 1 ? 2000 : 6000);
        if (TS.rateStrikes === 1) {
          dirtyAdd(key); /* keep the intent — it goes out after the backoff */
        } else {
          TS.dirty.length = 0; TS.desired = {};
          revertFrom(TS.lastCart);
          setCartError('Your cart is updating a little slowly. Give it a moment and try again.');
        }
        return;
      }
      if (c.kind === 'network') { handleNetwork(); return; }
      if (c.kind === 'stalekey') {
        /* 8/16: variantOf, not the bare map — the map is populated now (noteVariants),
           but a key minted by a correction we never observed still parses its variant
           out of the key prefix. Before this the lookup was always undefined and the
           whole recovery below was skipped, silently eating the shopper's tap. */
        var wantedVariant = variantOf(key);
        delete TS.desired[key];
        TS.dirty = TS.dirty.filter(function (k) { return k !== key; });
        return fetch(window.routes.cart_url + '.js', { headers: { Accept: 'application/json' } })
          .then(function (rr) { return rr.json(); })
          .then(function (fresh) {
            TS.lastCart = fresh;
            var moved = false;
            /* 2026-08-16: populating TS.keyVariant ARMED this loop, which had been dead
               since birth — so its safety now matters for the first time. It takes the
               FIRST line matching the variant, and a cart can legitimately hold TWO
               lines of one variant (different gift properties leave them unmerged). On
               such a cart it would alias the dead key onto the WRONG line and write that
               line's ABSOLUTE quantity — money on the wrong row. If the variant is
               ambiguous we do not guess: fall through to a plain repaint, which is
               exactly the pre-8/16 behaviour (the tap is dropped, visibly and safely). */
            var matches = 0;
            for (var m = 0; m < fresh.items.length && wantedVariant; m++) {
              if (fresh.items[m].id === wantedVariant) matches++;
            }
            if (matches > 1) wantedVariant = null;
            for (var i = 0; i < fresh.items.length && wantedVariant; i++) {
              if (fresh.items[i].id === wantedVariant && fresh.items[i].key !== key) {
                TS.keyAlias[key] = fresh.items[i].key;        /* forward it for every future tap */
                TS.desired[fresh.items[i].key] = quantity;   /* the shopper's intent, preserved */
                dirtyAdd(fresh.items[i].key);
                moved = true;
                break;
              }
            }
            stampKeys(fresh);   /* the DOM must stop handing back the dead key */
            paint(fresh);
            if (moved) pump();
          })
          .catch(function () { return refresh({ open: false }); });
      }
      if (c.kind === 'notfound') {
        /* another tab changed the cart; the key is stale. No error for the shopper. */
        delete TS.desired[key];
        TS.dirty = TS.dirty.filter(function (k) { return k !== key; });
        return refresh({ open: false });
      }
      if (c.kind === 'unknown') { revertFrom(TS.lastCart); setCartError(tsSafeText(c.text)); return; }

      var cart = r.payload;
      TS.rateStrikes = 0;
      TS.lastCart = cart;
      showSpinner(key, false);

      if (c.kind === 'quantity') {
        /* server rejected or clamped; still authoritative */
        setLineError(key, tsSafeText(c.text));
      }

      /* Do not render an intermediate state — more intent is queued. */
      if (TS.dirty.length) return;

      paint(cart, cart.sections);
      reconcile(cart, key, quantity);
      TS.desired = {};
      /* Gift correction is NOT called here: pump() still holds TS.inFlight at this
         point, so maybeCorrectGifts would bail on its own guard and gifts would never
         recompute (observed live: a 1-alarm cart still promising all four gifts).
         pump() runs it once the queue has genuinely drained. */
    }).catch(function () {
      handleNetwork();
    });
  }

  function handleNetwork() {
    TS.dirty.length = 0; TS.desired = {};
    revertFrom(TS.lastCart);
    setCartError('You appear to be offline. Your cart was not changed.');
  }

  function revertFrom(cart) {
    setBusy(false);
    if (!cart) return;
    cart.items.forEach(function (it) {
      inputsFor(it.key).forEach(function (i) { i.value = String(it.quantity); });
      showSpinner(it.key, false);
    });
  }

  /* After a paint, set every input from the authoritative cart, matched BY KEY. */
  function reconcile(cart, writtenKey, requested) {
    var byKey = {};
    cart.items.forEach(function (it) {
      byKey[it.key] = it;
      inputsFor(it.key).forEach(function (i) { i.value = String(it.quantity); });
    });
    if (writtenKey == null) return;
    var line = byKey[writtenKey];
    if (!line) return; /* stepped to 0 and gone — the success path, no message */
    if (line.quantity !== requested) {
      var msg = (window.cartStrings && window.cartStrings.quantityError)
        ? window.cartStrings.quantityError.replace('[quantity]', line.quantity)
        : 'You can only add ' + line.quantity + ' of this item to your cart.';
      setLineError(writtenKey, msg);
      announce(msg);
    }
  }

  /* ------------------------------------------------------------- the pump -- */
  function pump() {
    if (TS.inFlight) return;
    var wait = TS.stallUntil - Date.now();
    if (wait > 0) { setTimeout(pump, wait); return; }
    if (!TS.dirty.length) return;
    /* Pace, don't refuse: the intent stays queued and goes out a moment later, so the
       shopper never sees an error and never loses the quantity they asked for. */
    if (rateLimited()) { setTimeout(pump, 400); return; }
    var key = TS.dirty.shift();
    var quantity = TS.desired[key];
    if (quantity == null) { pump(); return; }
    TS.inFlight = { key: key, quantity: quantity };
    write(key, quantity).catch(function () {}).then(function () {
      TS.inFlight = null;
      if (TS.dirty.length) { pump(); return; }
      /* Queue is drained. Settle: force every input back to the authoritative cart
         (an optimistic value from the final tap can otherwise be left showing — seen
         live as stepper 0 on a cart of 1), then run the gift tier once. */
      settle();
    });
  }

  function flushSettleWaiters() {
    var ws = TS.settleWaiters;
    if (!ws.length) return;
    TS.settleWaiters = [];
    for (var i = 0; i < ws.length; i++) { try { ws[i](TS.lastCart); } catch (e) {} }
  }

  function settle() {
    if (TS.inFlight || TS.dirty.length) return;
    var cart = TS.lastCart;
    /* Nothing to reconcile against, but a caller may still be waiting on us — a promise
       that never settles leaves the PDP button disabled for good. */
    if (!cart) { flushSettleWaiters(); return; }
    reconcile(cart, null, null);
    var correcting = maybeCorrectGifts(cart);
    if (!TS.settleWaiters.length) return;
    /* An add routed through the quantity path (see mergeTargetFor) only finishes here.
       Release the caller AFTER the gift correction, so "Buy Now" cannot navigate to
       /checkout and abort the write that fixes the gifts — but never wait more than
       1.5s, because a live button beats a perfect one. */
    var done = false;
    var t = setTimeout(function () { if (!done) { done = true; flushSettleWaiters(); } }, 1500);
    Promise.resolve(correcting).catch(function () {}).then(function () {
      if (done) return;
      done = true;
      clearTimeout(t);
      flushSettleWaiters();
    });
  }

  /* ------------------------------------------------------------- gift tier -- */
  /* Greedy 6/4/2 pack decomposition, ported from the retired ts-cart-sync.js.
     qty 10 = 6+4 -> guide, 2x lock, 1x cam, 1x sign. The guide is emailed so it
     stays 1x. Key naming matches exactly what the PDP writes, so a plain pack add
     needs no rewrite at all. */
  function giftProps(qty) {
    /* 2026-08-12 OFFER CHANGE: the 6-pack is $99 ($16.50/alarm) with the ebook and
       TWO no-drill window locks. The dummy camera and the yard sign are DROPPED —
       they cost ~$12 landed and funded most of the $20.94 price cut.
         qty 1     -> nothing
         qty 2-3   -> guide
         qty 4-5   -> guide + 1 lock
         qty 6+    -> guide + 2 locks per full six, +1 for a 4-5 remainder
       The guide is emailed, so it never multiplies. Key naming must stay identical
       to what the PDP writes, or a plain pack add triggers a pointless rewrite. */
    var locks = Math.floor(qty / 6) * 2 + ((qty % 6) >= 4 ? 1 : 0);
    var list = [];
    if (qty >= 2) list.push([1, 'Home Security Guide']);
    if (locks > 0) list.push([locks, 'No-Drill Window Lock']);
    function fmt(g) { return g[0] > 1 ? g[0] + '× ' + g[1] : g[1]; }
    var props = {};
    if (list.length === 1) props['Free gift'] = fmt(list[0]);
    else list.forEach(function (g, i) { props['Free gift ' + (i + 1)] = fmt(g); });
    return props;
  }

  /* 2026-09-25 (B4) — A STABLE GIFT CARRIER. The gifts used to live on `firstKey`, the
     FIRST alarm line in cart.items, and add.js inserts a new line at the TOP. So Black
     6-pack (gifts) + a White single moved the gifts onto White even though 7 alarms earn
     exactly the gifts 6 do: two property writes, two new keys, and the drawer painted the
     gifts under Black, then 1-4 s later jumped them to White and reflowed the rows under
     the shopper's thumb (independent QA 2026-09-25, reproduced live and in the harness).
     The carrier is now the first alarm line that ALREADY carries a non-empty Free gift*
     value; only when no line carries any (fresh cart, or the carrier was removed) does it
     fall back to the first alarm line. With two carriers (two PDP pack adds) the first
     carrying line in cart order keeps them and the other is cleared, as before.
     fusedGiftProps uses the same rule, so a stepper write and the corrector always agree. */
  function hasGiftValue(it) {
    var p = it && it.properties;
    if (!p) return false;
    return Object.keys(p).some(function (k) { return k.indexOf('Free gift') === 0 && !!p[k]; });
  }
  function giftCarrierKey(cart) {
    var firstKey = null, carrier = null;
    if (!cart || !cart.items) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.product_id !== ALARM_PRODUCT_ID) continue;
      if (firstKey === null) firstKey = it.key;
      if (carrier === null && hasGiftValue(it)) carrier = it.key;
    }
    return carrier !== null ? carrier : firstKey;
  }

  /* Runs INSIDE the queue, never as a parallel layer, and only once the shopper
     has stopped. Clearing a property requires writing '' — Shopify silently
     ignores an omitted key (it means "nothing supplied", never "remove"). */
  function maybeCorrectGifts(cart) {
    if (TS.dirty.length || TS.inFlight) return;
    if (TS.giftBudget <= 0) return;

    /* 2026-09-24 — A LONE ALARM LINE IS RE-TIERED TOO. This pass used to bail at
       alarmLines <= 1, because a write TO the only alarm line already fuses the right
       gifts (fusedGiftProps). But a write to ANOTHER line changes the tier as well,
       and fusedGiftProps only ever looks at the line being written. Reviewer QA
       2026-09-23: Black x6 + White x1 puts the gifts on White (the first alarm line);
       Remove White and Black x6 was left at $99.00 with every gift blanked to "", a
       paid 6-pack with no guide and no locks, until the shopper touched its stepper.
       The bail dates from 8/12, when this write ran OUTSIDE the lane and killed the key
       that + and Remove were about to post. Since H2 (8/16, below) it holds the lane
       and the new key is stamped by paint() after the repaint, so one line now gets the
       same leased POST as many: same quantity + the fused properties, one atomic key
       rotation. When the gifts already match, `same` skips it and nothing is sent.
       A tap landing during that one write is parked and goes out after it, forwarded
       to the new key by forwardReplaced (without it: one 400, then a recovery). */
    var total = 0, firstKey = null;
    cart.items.forEach(function (it) {
      if (it.product_id !== ALARM_PRODUCT_ID) return;
      total += it.quantity;
      if (firstKey === null) firstKey = it.key;
    });
    if (firstKey === null) return;
    var carrierKey = giftCarrierKey(cart);   /* B4: stable carrier, falls back to firstKey */

    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      if (it.product_id !== ALARM_PRODUCT_ID) continue;
      var have = it.properties || {};
      var want = it.key === carrierKey ? giftProps(total) : {};
      var target = {};
      Object.keys(have).forEach(function (k) { if (k.indexOf('Free gift') !== 0) target[k] = have[k]; });
      Object.keys(want).forEach(function (k) { target[k] = want[k]; });
      Object.keys(have).forEach(function (k) { if (k.indexOf('Free gift') === 0 && !(k in want)) target[k] = ''; });

      var same = true, all = {};
      Object.keys(have).concat(Object.keys(target)).forEach(function (k) { all[k] = 1; });
      Object.keys(all).forEach(function (k) { if ((have[k] || null) !== (target[k] || null)) same = false; });
      if (same) continue;

      /* 2026-08-16 (H2) — THE CORRECTION MUST RESERVE THE WRITE LANE.
         This POST rewrites properties, and properties are part of the line-key hash, so
         it MINTS A NEW KEY and kills the old one. It used to run with TS.inFlight still
         null, and pump() gates on nothing else — so a tap landing inside this window
         took the lane and posted the very key this request was about to destroy.
         Shopify answers 400 "no valid id or line parameter"; two of those showed up in a
         clean 6 -> 8 -> 5 -> Remove run, and Remove posts to that same dead key.
         Holding the lane makes the correction VISIBLE to pump(): the tap is parked in
         TS.desired/TS.dirty (last-intent-wins already coalesces it) and goes out one
         round trip later. (9/24: "against the key stampKeys hands back" was wrong while
         a tap is parked, because nothing repaints before it goes out; forwardReplaced
         now carries it to the new key.) Deferred, never dropped, and no extra request.
         The lease is an OBJECT and every release is identity-checked, so a hung or late
         response can never free a lane another writer now owns. */
      var lease = { key: it.key, quantity: it.quantity, gift: true };
      TS.inFlight = lease;
      var laneTimer = setTimeout(function () {
        /* Liveness guard: fetch never times out on its own. Releasing early degrades to
           exactly the pre-fix behaviour, which the stalekey path recovers from, whereas
           holding forever freezes the shopper's cart outright. */
        if (TS.inFlight === lease) { TS.inFlight = null; pump(); }
      }, GIFT_WRITE_TIMEOUT_MS);
      var releaseLane = function () {
        clearTimeout(laneTimer);
        if (TS.inFlight === lease) TS.inFlight = null;
      };

      TS.giftBudget--;
      TS.writeLog.push(Date.now());
      return post(window.routes.cart_change_url, {
        id: it.key,
        quantity: it.quantity,
        properties: target,
        sections: sectionIds().join(','),
        sections_url: window.location.pathname,
      }).then(function (r) {
        /* Release FIRST. maybeCorrectGifts and pump both bail on their own TS.inFlight
           guard, so recursing or pumping while still holding the lane is a silent
           no-op — the same trap already documented for write()/pump() above. */
        releaseLane();
        var c = classify(r, false);
        if (c.kind !== 'ok' && c.kind !== 'quantity') { pump(); return; } /* truth already painted */
        TS.lastCart = r.payload;
        forwardReplaced(it, r.payload);   /* a parked tap must not post the key this write killed */
        /* Deliberately NO stampKeys() here. stampKeys pairs DOM rows to cart items by
           POSITION, and at this point the DOM has NOT been repainted yet — stamping a
           stale row set writes the wrong key onto the wrong row. paint() below already
           calls stampKeys AFTER the repaint, which is the only correct moment. */
        if (TS.dirty.length) { pump(); return; }
        paint(r.payload, r.payload.sections);
        return maybeCorrectGifts(r.payload);
      }).catch(function () { releaseLane(); pump(); });
    }
  }

  /* ----------------------------------------------------------- public API -- */
  function setLineQuantity(key, quantity) {
    key = forwardKey(key);
    if (!key) return;
    TS.giftBudget = GIFT_CORRECTIONS_PER_ACTION;
    TS.desired[key] = quantity;
    dirtyAdd(key);
    setCartError('');
    setLineError(key, '');
    inputsFor(key).forEach(function (i) { i.value = String(quantity); });
    showSpinner(key, true);
    setBusy(true);
    pump();
  }

  function refresh(opts) {
    return fetch(window.location.pathname + '?sections=' + sectionIds().join(','), {
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    }).then(function (r) { return r.json(); }).then(function (sections) {
      return fetch(window.routes.cart_url + '.js').then(function (r) { return r.json(); }).then(function (cart) {
        TS.lastCart = cart;
        paint(cart, sections);
        if (opts && opts.open) openDrawer();
        return cart;
      });
    }).catch(function () {});
  }

  function applyAddResponse(res, opts) {
    var sections = res && res.sections;
    var count = null;
    if (sections && sections['cart-drawer']) {
      var doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      var badge = doc.querySelector('.ts-cart-count');
      if (badge) count = parseInt(badge.textContent, 10);
    }
    /* The add response has no item_count of its own, but the section it returns
       was server-rendered against the post-add cart, so .ts-cart-count is
       authoritative — no extra /cart.js round trip. */
    paint({ item_count: isNaN(count) || count == null ? 1 : count }, sections);
    if (opts && opts.open) openDrawer();
    /* refresh lastCart in the background so gift corrections have truth to work from.
       8/16: also parked on TS.cartSync. A second add arriving before this lands would
       otherwise read the PRE-add cart, find no line for the variant, and go straight
       back down the merging add.js path that H1 exists to avoid. */
    TS.cartSync = fetch(window.routes.cart_url + '.js').then(function (r) { return r.json(); }).then(function (cart) {
      TS.lastCart = cart;
      stampKeys(cart);
      TS.giftBudget = GIFT_CORRECTIONS_PER_ACTION;
      maybeCorrectGifts(cart);
    }).catch(function () {});
  }

  /* 2026-08-16 (H1) — A REPEAT ADD THAT MERGES LOSES ITS GIFTS.
     /cart/add.js folds an added line into an existing one when the variant AND the
     properties match byte for byte. The PDP computes giftProps() from the BUTTON
     quantity, so "add 3" twice posts {"Free gift":"Home Security Guide"} twice, Shopify
     merges them into ONE line of 6 at $99.00 — and that line still carries a 3-pack's
     gifts, with no window lock at all. Reproduced live.
     maybeCorrectGifts is the fallback, not the fix: since 2026-09-24 it would re-tier
     a lone merged line, but only as a SECOND write after the add, and a tap during it
     can post the key it is replacing (the 400 "no valid id" family, recovered).
     So the blind merge never happens. If the cart already holds a line for the variant,
     the add IS a quantity change on that line: it goes through the one writer, and
     write()'s existing fusedGiftProps computes gifts from the RESULTING total and fuses
     them into the SAME POST. One request instead of two, one atomic key rotation. */
  function mergeTargetFor(item) {
    var cart = TS.lastCart;
    if (!cart || !cart.items) return null;      /* no truth yet -> plain add, as today */
    var vid = Number(item && item.id);
    if (!vid) return null;
    for (var i = 0; i < cart.items.length; i++) {
      var it = cart.items[i];
      var iv = Number(it.variant_id != null ? it.variant_id : it.id);
      if (iv === vid) return it;                /* first match wins */
    }
    return null;
  }

  function bumpLine(line, item, opts) {
    var q = Number(item.quantity);
    if (!(q > 0)) q = 1;
    /* add.js quantity is a DELTA; change.js quantity is ABSOLUTE. */
    var want = line.quantity + q;
    TS.keyVariant[line.key] = Number(line.variant_id != null ? line.variant_id : line.id);
    return new Promise(function (resolve) {
      /* HARD liveness floor. The caller (the PDP button) stays disabled with a spinner
         until this resolves, so a queue that never drains — a dropped socket, a write
         that errors in a path we did not foresee — would leave the shopper unable to
         buy. settle() has its own 1.5s cap, but settle() might never be REACHED; this
         one cannot be skipped. Resolving early is harmless: the cart is authoritative
         and repaints itself either way. */
      var done = false;
      var fire = function () { if (done) return; done = true; resolve(TS.lastCart); };
      var t = setTimeout(fire, 6000);
      TS.settleWaiters.push(function (cart) { clearTimeout(t); if (!done) { done = true; resolve(cart); } });
      setLineQuantity(line.key, want);
    }).then(function (cart) {
      cart = cart || TS.lastCart;
      /* THE ADD MUST REJECT IF IT DID NOT HAPPEN. addNew() rejects on a failed POST, and
         the PDP relies on that: tsSubmit's .catch re-enables the button and sends the
         shopper to /cart. This path resolves off the settle queue, which swallows write
         failures, so without this check a FAILED add would look like a success — and on
         "Buy Now" that means navigating to /checkout with the pack never added. Confirm
         against the authoritative cart that the variant really reached the quantity we
         asked for; anything less is a failure, whatever the queue thinks. */
      var got = 0, vid = Number(item.id);
      if (cart && cart.items) {
        for (var i = 0; i < cart.items.length; i++) {
          var it = cart.items[i];
          if (Number(it.variant_id != null ? it.variant_id : it.id) === vid) got += it.quantity;
        }
      }
      if (got < want) throw new Error('addfailed');
      /* Opened only after the authoritative paint, so the drawer never flashes the
         pre-add quantity — the same moment applyAddResponse opens it on the add path. */
      if (opts.open !== false) openDrawer();
      return cart;
    });
  }

  function add(items, opts) {
    opts = opts || {};
    if (items && items.length === 1) {
      /* Wait on the /cart.js refresh ALREADY in flight rather than firing another —
         no extra request, and it closes the fast-double-tap window in which lastCart
         is still the pre-add cart. */
      return Promise.resolve(TS.cartSync).catch(function () {}).then(function () {
        var line = mergeTargetFor(items[0]);
        if (!line) return addNew(items, opts);
        /* NO keyIsLive() guard here — it would be a tautology. mergeTargetFor reads the
           line OUT OF TS.lastCart and keyIsLive checks membership OF TS.lastCart, so it
           is true by construction and proves nothing about the real cart. TS.lastCart is
           a cache, never proof: another tab, an expired cart or an out-of-band change
           can all make that key dead. Caught live — clearing the cart behind the page
           made the next add post a dead key and fail.
           So the bump is allowed to fail, and a failure falls back to a plain add. The
           shopper still gets their pack; the extra request happens ONLY on this path. */
        return bumpLine(line, items[0], opts).catch(function () {
          return addNew(items, opts);
        });
      });
    }
    return addNew(items, opts);
  }

  function addNew(items, opts) {
    TS.writeLog.push(Date.now());
    return post(window.routes.cart_add_url, {
      items: items,
      sections: sectionIds().join(','),
      sections_url: window.location.pathname,
    }).then(function (r) {
      var c = classify(r, false);
      if (c.kind === 'ratelimit') { setCartError('Your cart is updating a little slowly. Give it a moment and try again.'); throw new Error('ratelimit'); }
      if (c.kind !== 'ok' && c.kind !== 'quantity') { setCartError(tsSafeText(c.text)); throw new Error(c.kind); }
      applyAddResponse(r.payload, { open: opts.open !== false });
      return r.payload;
    });
  }

  function openDrawer() {
    var d = document.querySelector('cart-drawer');
    if (d && typeof d.open === 'function') d.open();
  }
  function closeDrawer() {
    var d = document.querySelector('cart-drawer');
    if (d && typeof d.close === 'function') d.close();
  }

  window.TSCart = {
    setLineQuantity: setLineQuantity,
    removeLine: function (key) { setLineQuantity(key, 0); },
    add: add,
    /* 2026-08-16: exported so the PDP writes the SAME strings at add time that this
       file writes on a quantity change. Before this, the PDP had its own name-only
       list with no multiplier, so a 6-pack added straight from the page shipped
       "No-Drill Window Lock" instead of "2× No-Drill Window Lock" — identical to a
       4-pack — and only corrected itself if the shopper happened to touch the
       stepper. One order (#tremshield10881) went out that way. ONE producer now. */
    giftProps: giftProps,
    refresh: refresh,
    applyAddResponse: applyAddResponse,
    paint: paint,
    openDrawer: openDrawer,
    closeDrawer: closeDrawer,
    getCart: function () { return TS.lastCart; },
    _state: TS,
  };

  /* --------------------------------------------------------- delegation --- */
  /* Bound once on document, so it survives every repaint with zero re-binding.
     This replaces Dawn's <cart-remove-button> constructor and CartItems' own
     debounced change listener. <quantity-input> (global.js) still does the
     stepping and dispatches a bubbling `change` — it needs no modification. */
  document.addEventListener('change', function (e) {
    var i = e.target.closest && e.target.closest('input.quantity__input[data-line-key]');
    if (!i) return;
    var q = parseInt(i.value, 10);
    var key = resolveKey(i, i.getAttribute('data-line-key'));
    if (!key) return;
    setLineQuantity(key, isNaN(q) || q < 0 ? 0 : q);
  });

  document.addEventListener('click', function (e) {
    var r = e.target.closest && e.target.closest('cart-remove-button[data-line-key]');
    if (!r) return;
    e.preventDefault(); /* also neutralises the /cart page's <a href=url_to_remove> */
    var key = resolveKey(r, r.getAttribute('data-line-key'));
    if (!key) return;
    setLineQuantity(key, 0);
  });

  /* ------------------------------------------------------------ resyncs --- */
  /* Ported from ts-cart-sync.js: a back-navigation can be a FULL page load served
     from Shopify's anonymous page cache, whose HTML was rendered for a different
     cart. Seen live: real cart 6, drawer painted empty, checkout disabled. */
  window.addEventListener('pageshow', function () {
    fetch(window.routes.cart_url + '.js').then(function (r) { return r.json(); }).then(function (cart) {
      TS.lastCart = cart;
      var host = document.querySelector('cart-drawer');
      var renderedEmpty = host ? host.classList.contains('is-empty') : null;
      var badge = document.querySelector('.ts-cart-count');
      var renderedCount = badge ? parseInt(badge.textContent, 10) : null;
      if (renderedEmpty !== (cart.item_count === 0) || renderedCount !== cart.item_count) refresh({ open: false });
      else setHeaderCount(cart.item_count);
    }).catch(function () {});
  });

  /* second tab: resync when this one regains focus */
  document.addEventListener('visibilitychange', function () {
    if (document.visibilityState !== 'visible') return;
    if (TS.inFlight || TS.dirty.length) return;
    fetch(window.routes.cart_url + '.js').then(function (r) { return r.json(); }).then(function (cart) {
      var prev = TS.lastCart;
      TS.lastCart = cart;
      if (!prev || prev.item_count !== cart.item_count) refresh({ open: false });
    }).catch(function () {});
  });

  /* Stamp authoritative keys as early as possible so the first tap is already valid. */
  (function initKeys() {
    /* Parked on TS.cartSync for the same reason as applyAddResponse: the very first add
       on a page must be able to WAIT for this rather than decide from a null cart. */
    TS.cartSync = fetch(window.routes.cart_url + '.js').then(function (r) { return r.json(); }).then(function (cart) {
      TS.lastCart = cart;
      stampKeys(cart);
    }).catch(function () {});
  })();

  window.addEventListener('online', function () { refresh({ open: false }); });
})();
